import { AbstractRenderer, AnimatedSprite, BLEND_MODES, Graphics, RenderTexture, Texture } from "pixi.js"

import { BaseRenderable } from "./base_renderable"

// In-world campfire flame rendered from a PROCEDURALLY BAKED animation instead of hand-drawn atlas
// frames. A pool of rising particles (the ember roster's flame look, minus the halo) is baked once
// into a small set of RenderTextures; every fire plays those back through a plain AnimatedSprite.
// Playback is a GPU texture swap per frame (the same cost as the old atlas "fire" sprite) — far
// cheaper than redrawing a Graphics every frame per fire.
//
// SEAMLESS LOOP: unlike the roster's real-time stochastic sim, the bake is PHASE-based. Every
// particle is a pure function of a loop phase u in [0,1): its age is frac(u - birthPhase), so the
// frame at u=1 is identical to u=0 and the AnimatedSprite loops with no jump. Particles fade to zero
// alpha just before their age wraps (top -> bottom), so the invisible reset is never seen; staggered
// birth phases mean no two reset together.

// Bake resolution / length. The flame fills the frame; height gives the particles room to rise. More
// frames = smoother motion + more VRAM; the loop is seamless at any count.
const FRAME_W = 48
const FRAME_H = 64
const FRAME_COUNT = 30
const PARTICLE_COUNT = 52

// Playback tuning (world-space). Tweak scale to size the flame against a 32px tile.
const FIRE_SCALE = 0.8
const ANIMATION_SPEED = 0.4
// Nudge the flame's base down a few px so it reads as emanating from inside the campfire logs
// rather than sitting right on top of them.
const FIRE_Y_OFFSET = 5
// Horizontal nudge of the flame within its container (the fire entity is placed a few px right of
// the campfire centre server-side); negative pulls it left to sit centred over the logs.
const FIRE_X_OFFSET = -3
// ADD makes the warm glow bloom onto the world (great at night, like the roster card; can look
// washed-out over bright daytime terrain). Switch to BLEND_MODES.NORMAL if daytime fires read weak.
const FIRE_BLEND = BLEND_MODES.ADD

// Campfire flame palette (young -> old): hot orange core -> orange -> red-orange -> deep red.
// Warmer/redder than the roster's golden flame (no white/yellow), and with ADD blend the dense base
// still brightens toward a hot core. Tweak these hexes to push the fire more red or more orange.
const campfireColor = (lifeFrac: number): number =>
    lifeFrac > 0.85 ? 0xff9d2b : lifeFrac > 0.58 ? 0xff6a12 : lifeFrac > 0.3 ? 0xdc3a0c : 0xa81805

// Baked once and shared across every fire instance.
let cachedFrames: Texture[] | undefined

// A single particle's fixed, per-loop attributes. Everything is evaluated from the loop phase at
// bake time, so the motion is deterministic within a loop and repeats exactly.
interface LoopParticle {
    birthPhase: number // when in the loop this particle is "youngest" (at the base)
    baseX: number // horizontal spawn offset from centre
    baseY: number // vertical spawn (near the bottom)
    rise: number // how far it travels up over one lifetime (varies => tongues of different height)
    size: number // base radius
    drift: number // slow lateral drift over life
    converge: number // how strongly it pulls toward centre as it rises (narrows the flame upward)
    wobbleAmp: number // lateral wobble amplitude
    wobbleFreq: number // lateral wobble cycles per lifetime
    wobblePhase: number
}

const frac = (n: number): number => n - Math.floor(n)

const makeLoopParticles = (): LoopParticle[] => {
    const cx = FRAME_W / 2
    const particles: LoopParticle[] = []
    for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
            // Evenly staggered around the loop (+ small jitter) so density is steady, not pulsing.
            birthPhase: frac(i / PARTICLE_COUNT + (Math.random() - 0.5) * (0.4 / PARTICLE_COUNT)),
            baseX: cx + (Math.random() - 0.5) * FRAME_W * 0.5,
            baseY: FRAME_H - Math.random() * 5,
            rise: FRAME_H * (0.55 + Math.random() * 0.5),
            size: 3.5 + Math.random() * 3.5,
            drift: (Math.random() - 0.5) * FRAME_W * 0.1,
            converge: 0.3 + Math.random() * 0.4,
            wobbleAmp: 1.5 + Math.random() * 3,
            wobbleFreq: 0.5 + Math.random() * 1.5,
            wobblePhase: Math.random() * Math.PI * 2,
        })
    }
    return particles
}

// Draw one loop frame (phase in [0,1)) of the particle pool into `g`. Additive-style bright core is
// left to the playback blend; here each particle is a soft warm circle whose alpha envelope is 0 at
// both ends of its life (so the top->bottom wrap is invisible) and brightest near the base.
const drawLoopFrame = (g: Graphics, particles: LoopParticle[], phase: number) => {
    g.clear()
    const cx = FRAME_W / 2
    for (const p of particles) {
        const age = frac(phase - p.birthPhase) // 0 (youngest, at base) -> 1 (oldest, at top)
        const lifeFrac = 1 - age
        const fadeIn = Math.min(1, age / 0.12) // kill the pop as age wraps back to 0 at the base
        const env = fadeIn * (1 - age) // 0 at both ends, brightest near the base
        if (env <= 0.02) {
            continue
        }
        const y = p.baseY - p.rise * age
        const xBase = p.baseX + (cx - p.baseX) * (age * p.converge) + p.drift * age
        const x = xBase + p.wobbleAmp * Math.sin(p.wobbleFreq * age * Math.PI * 2 + p.wobblePhase)
        const r = p.size * (0.5 + 0.5 * lifeFrac)
        g.beginFill(campfireColor(lifeFrac), env * 0.9)
        g.drawCircle(x, y, r)
        g.endFill()
    }
}

// Bake FRAME_COUNT seamless loop frames into RenderTextures. Cached: only the first fire in a
// session pays for the bake; every later fire reuses the same textures.
const bakeFireFrames = (renderer: AbstractRenderer): Texture[] => {
    if (cachedFrames) {
        return cachedFrames
    }
    const particles = makeLoopParticles()
    const g = new Graphics()
    const frames: Texture[] = []
    for (let i = 0; i < FRAME_COUNT; i++) {
        drawLoopFrame(g, particles, i / FRAME_COUNT)
        const rt = RenderTexture.create({ width: FRAME_W, height: FRAME_H })
        renderer.render(g, { renderTexture: rt, clear: true })
        frames.push(rt)
    }
    g.destroy()
    cachedFrames = frames
    return frames
}

export class FireSprite extends BaseRenderable {
    private animation: AnimatedSprite

    constructor(renderer: AbstractRenderer) {
        super()
        const frames = bakeFireFrames(renderer)
        this.animation = new AnimatedSprite(frames)
        // Bottom-centre anchored so the flame's base sits on the fire entity's location.
        this.animation.anchor.set(0.5, 1)
        this.animation.x = FIRE_X_OFFSET
        // Additive, like the roster card: the warm glow adds onto the (night-time) world.
        this.animation.blendMode = FIRE_BLEND
        this.animation.animationSpeed = ANIMATION_SPEED
        // Start on a random frame so multiple fires don't flicker in lockstep.
        this.animation.gotoAndPlay(Math.floor(Math.random() * frames.length))
        this.addChild(this.animation)
        this.setIntensity(1)
    }

    // Fire strength (0..1) -> flame height + brightness, so a dying fire reads as a smaller, dimmer
    // flame. Defaults to full strength; wire this to a per-fire strength signal when one exists.
    setIntensity = (strength: number) => {
        const s = Math.max(0, Math.min(1, strength))
        this.animation.alpha = 0.35 + 0.65 * s
        this.animation.scale.set(FIRE_SCALE, FIRE_SCALE * (0.5 + 0.5 * s))
    }

    // Anchored at its base (not the default -height*0.25); drop it slightly so the flame emanates
    // from within the campfire rather than perching on top.
    getYOffset() {
        return FIRE_Y_OFFSET
    }

    // The campfire drives a burn-down factor through the entity's spriteScale (1 -> 0 as the fire
    // ages). The marker-create path applies it via scale.set once; honour later updates here too so
    // a dying fire visibly shrinks toward its base. Composes with the internal FIRE_SCALE/intensity.
    updateScale(scale: number) {
        this.scale.set(scale, scale)
    }

    // Detach from the shared ticker when the fire entity is destroyed (AnimatedSprite.play registers
    // it there); the cached textures are shared and intentionally left alive.
    cleanup = () => {
        this.animation.stop()
    }
}
